import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import {
  Router,
  NavigationEnd,
  Event as RouterEvent,
} from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DeviceService } from './shared/services/client/device.service';
import { SocketService } from './shared/services/client/socket.service';
import { environment } from '../environments/environment';
import { CookieService } from './shared/services/cookie/cookie.service';
import { AppService, AppMeta } from './shared/services/app/app.service';
import { ModuleService } from './shared/services/module/module.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = environment.appName;
  mobileQuery: MediaQueryList;

  // Parent page info (now reactive)
  parentUrl$ = new BehaviorSubject<string | null>(null);
  parentOrigin$ = new BehaviorSubject<string | null>(null);

  /** true → splash screen visible, false → show the real app */
  showSplash = true;

  private subscriptions = new Subscription();
  private _mobileQueryListener: () => void;
  private _messageListener: (event: MessageEvent) => void;

  /**
   * Handles the dedicated "AppView metadata" postMessage
   * (services, prefix, routes, ngrok).
   *
   * Instead of caching this on the component (as before), we now push it
   * straight into `AppService`, so any component in the app — HomeComponent
   * in particular — can subscribe to `appService.meta$` and react to it.
   */
  private handleAppViewMeta = (event: MessageEvent): void => {
    // OPTIONAL: tighten security – only accept from the known origin
    if (event.origin !== this.allowedOrigin) {
      console.warn('[AppComponent] Ignored AppView meta from unknown origin', event.origin);
      return;
    }

    const data = event.data;
    if (
      data &&
      typeof data === 'object' &&
      Array.isArray(data.services) &&
      typeof data.prefix === 'string' &&
      Array.isArray(data.routes)
    ) {
      console.log('[AppComponent] Received AppView metadata →', data);
      this.appService.receiveMeta(data as AppMeta);
    }
  };

  /** Origin that we trust for postMessage traffic (derived from env). */
  private readonly allowedOrigin: string = new URL(environment.appDomain).origin;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private router: Router,
    private deviceService: DeviceService,
    private socketService: SocketService,
    private cookieService: CookieService,
    private appService: AppService,
    private moduleService: ModuleService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);

    /* ---------------------------------------------------------
       ONE listener for **all** postMessage traffic that the
       Angular root can receive (both the old parent‑url logic
       and the new messenger‑view notification).
       ------------------------------------------------------- */
    this._messageListener = (event: MessageEvent) => this.handlePostMessage(event);
  }

  ngOnInit(): void {
    // 1️⃣  Show the splash for a few seconds
    this.startSplash();

    // 2️⃣  Existing init logic (parent‑url detection, sockets …)
    this.setupParentUrlDetection();
    this.setupSocketParentUrlSync();
    this.moduleService.init().subscribe();
 // Reactive: full module list, auto-updates on refresh()
    this.moduleService.modules$.subscribe(modules => console.log(modules));

    // Reactive: just Business's public services / api map
    this.moduleService.getPublicServices('Business').subscribe(services => console.log(services));
    this.moduleService.getServiceApiMap('Business').subscribe(api => console.log(api));

    /* ---------------------------------------------------------
       Listen to *any* postMessage coming from the iframe child.
       This is needed now that the Angular app embeds the
       MessengerView / AppView as an <iframe>.
       ------------------------------------------------------- */
    window.addEventListener('message', this._messageListener);

    // Listener for AppView meta‑information
    window.addEventListener('message', this.handleAppViewMeta);
  }

  /** -------------------------------------------------
   *  Show the splash screen for exactly 900ms.
   *  ------------------------------------------------- */
  private startSplash(): void {
    this.showSplash = true;
    setTimeout(() => (this.showSplash = false), 900);
  }

  /* -----------------------------------------------------------------
   *  Existing parent‑url detection code – unchanged.
   * ----------------------------------------------------------------- */
  private setupParentUrlDetection(): void {
    if (this.isInIframe()) {
      // 1. Try direct same‑origin access
      try {
        const url = window.parent.location.href;
        const origin = window.parent.location.origin;
        this.setParentInfo(url, origin);
        return;
      } catch (e) {
        console.info('Cross‑origin iframe – using fallbacks');
      }

      // 2. Fallback to referrer
      if (document.referrer) {
        try {
          const referrer = new URL(document.referrer);
          this.setParentInfo(referrer.href, referrer.origin);
          return;
        } catch (e) {
          console.warn('Invalid referrer URL:', document.referrer);
        }
      }

      // 3. Request via postMessage (cross‑origin)
      this.deviceService.oInfoNotification('Info', 'Requesting parent page URL...');
      window.parent.postMessage({ type: 'REQUEST_PARENT_URL' }, '*');

      window.addEventListener('message', this._messageListener);

      // Timeout fallback
      setTimeout(() => {
        if (!this.parentUrl$.value) {
          this.deviceService.oErrorNotification(
            'Warning',
            'Parent page did not respond with URL after 10s'
          );
        }
      }, 10000);
    } else {
      console.info('App not running in iframe – parent detection skipped');
    }
  }

  /** ---------------------------------------------------------
   *  Centralised handling of **all** postMessage events that
   *  reach the Angular root.
   *
   *  1️⃣  OPTIONAL origin check – uses the `allowedOrigin`
   *      derived from the environment.
   *
   *  2️⃣  Cookie payload sent by `AppView` (type: 'COOKIES').
   *      We now persist to `CookieService` (localStorage, unchanged)
   *      AND push the same object into `AppService` so components
   *      can subscribe reactively via `appService.cookies$`.
   *
   *  3️⃣  Existing messenger‑view handling (unchanged).
   * --------------------------------------------------------- */
  private handlePostMessage(event: MessageEvent): void {
    if (event.origin !== this.allowedOrigin) {
      console.warn(
        '[MiniBrowser] Ignored message from untrusted origin:',
        event.origin
      );
      return;
    }

    // Run inside Angular zone so any UI updates would be detected.
    this.ngZone.run(() => {
      const data = event?.data;
      if (!data) return;

      // ---------------------------------------------------------
      // Handle cookie payload coming from AppView
      // ---------------------------------------------------------
      if (data.type === 'COOKIES' && data.payload?.cookies) {
        console.log('[AppComponent] Received cookies from SPA →', data.payload.cookies);

        // Keep persisting to localStorage (unchanged behaviour) …
        this.cookieService.saveAll(data.payload.cookies);

        // …and also broadcast them through AppService so any component
        // (HomeComponent, etc.) can react without touching localStorage.
        this.appService.receiveCookies(data.payload.cookies);
        return;
      }

      // ---------------------------------------------------------
      // Generic logger (keeps the old behaviour)
      // ---------------------------------------------------------
      if (data.source !== 'react-devtools-content-script') {
        // console.log('[MiniBrowser] message from iframe →', event);
      }
    });
  }

  private setParentInfo(url: string, origin: string): void {
    this.parentUrl$.next(url);
    this.parentOrigin$.next(origin);

    console.log('Parent URL set:', url);
    this.deviceService.oSuccessNotification(
      'Success',
      `Connected to parent: ${new URL(url).hostname}`
    );

    // Immediate send if socket is ready
    if (this.socketService.isConnected) {
      this.sendParentInfoToBackend(url, origin);
    }
  }

  private sendParentInfoToBackend(url: string, origin: string): void {
    this.socketService.socket.emit('parent-page-info', {
      parentUrl: url,
      parentOrigin: origin,
      timestamp: Date.now(),
      socketId: this.socketService.socketId$.value,
    });
  }

  private setupSocketParentUrlSync(): void {
    // Send parent info whenever socket connects (handles reconnects)
    this.subscriptions.add(
      this.socketService.isConnected$.subscribe((connected) => {
        if (
          connected &&
          this.parentUrl$.value &&
          this.parentOrigin$.value
        ) {
          this.sendParentInfoToBackend(
            this.parentUrl$.value,
            this.parentOrigin$.value
          );
        }
      })
    );
  }

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true; // safer default for cross‑origin
    }
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    window.removeEventListener('message', this._messageListener);
    window.removeEventListener('message', this.handleAppViewMeta);
    this.subscriptions.unsubscribe();
  }

  // -----------------------------------------------------------------
  // Needed for the origin‑check block – inject Angular zone.
  // -----------------------------------------------------------------
  private ngZone = { run: (fn: Function) => fn() };
}
